import React, { useState, useContext, useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { AppContext } from '@prisjakt/ui-app-context';
import loadable from '@loadable/component';
import 'isomorphic-unfetch';
import mergeWith from 'lodash/mergeWith';
import Panel from '@prisjakt/ui-panel';
import { ScrollElement } from '@prisjakt/ui-scroll';
import {
  getCurrentCampaign,
  getHeaderHeight,
  getSimilarProductsCompareLink,
  mapStateToProps,
  dispatchProps,
  longPageActive,
  fetchDeals,
} from './utils';
import { AppState } from '../../reducers';
import AdContainer from '../../components/ads';
import FeedbackButton from '../feedback/feedback-button';
import Lazy from '@prisjakt/ui-lazy';
import Modal from '../../components/modal';
import PageContent from '../../components/page-content';
import RecentlyVisitedProducts from '../../components/recently-visited';
import Recommendations from '../../components/recommendations';
import StickyTrack from '../../components/sticky-track';
import TabList from './components/tab-list';
import { ViewportContext } from '../../components/viewport';
import { readPersonalizationSettings } from '../user/storage/personalization';
import productPageQuery from './query.graphql';
import {
  PRODUCT_ALERT_MODAL_ID,
  PRODUCT_CONTEXT_ID,
  mapOptionsToFilters,
  initialFilterState,
} from './constants';

import { ProductPageQuery } from '../../types';
import { CurrentCampaign, ProductPageProps } from './types';
import {
  ModelType,
  ModelTypeEnum,
} from '../../components/recommendations/types';
import { setSelectedVariant, setSelectedCondition } from './actions/product';
import { trackComparisonLinkClick } from '../product-compare/tracking';
import { TabContentWrapper, ProductPage as StyledProductPage } from './styles';
import {
  Variables,
  RouteReducerState,
  RouteReducerActions,
  Loaded,
  Group,
} from './product-page-types';
import ProductPageHeader from './components/product-header';
import ProductTabs from './components/product-tabs';
import ProductDuringPromoBanner from './components/product-promo-banner/product-during-promo-banner';
import ProductBreadCrumb from './components/product-breadcrumb';
import ProductBeforePromoBanner from './components/product-promo-banner/product-before-promo-banner';
import ProductScrollTopButton from './components/product-scroll-top-button';
import ProductCampaignDeals from './components/product-campaign-deals';
import { PriceListFilterContextContainer } from './price-list-filter-context';
import { useEffectOnMount } from '../../hooks/useEffectOnMount';
import ProductNavigation from './components/product-section-navigation';
import MobileTopAd from './components/price-list/ads/mobile-top-ad';

const ProductAlertModalContent = loadable(() =>
  import(
    /* webpackChunkName: "extra" */ '../product-alert/components/product-alert-modal-content'
  )
);

const ProductPage = ({
  activeCampaign,
  data,
  fetchMoreData,
  routeSection = 'main',
  campaignBanners,
  currentModal,
  me,
  review,
  hideModal,
}: ProductPageProps) => {
  const { t, linkTo, market } = useContext(AppContext);
  const [isHeaderVisible, setHeaderVisible] = useState<boolean>(true);
  const [campaigns, setCampaignsState] = useState([]);
  const dispatch = useDispatch();
  const { product, importantDates, recommendations } = data;
  const selectedCondition = useSelector(
    (state: AppState) => state.areas.product.condition
  );

  useEffectOnMount(() => {
    // Reset variants and conditions
    dispatch(setSelectedVariant({ id: 0 }));
    dispatch(setSelectedCondition('new'));
  });

  useEffect(() => {
    if (!IS_BROWSER || !product || !product.id) {
      return;
    }

    fetchDeals(market, product.id).then(
      res =>
        res.hits &&
        res.hits.hits &&
        setCampaignsState(
          res.hits.hits.map((hit: any) => ({
            id: hit._source.campaign.id,
            price: { id: hit._source.priceInfo.priceId.toString() },
          }))
        )
    );
  }, [product && product.id]); // eslint-disable-line

  if (!product) return null;

  product.campaigns = campaigns;

  const privacyPolicyUri = market?.privacySettings?.responsive?.privacyUrl;

  const currentCampaign = getCurrentCampaign(
    activeCampaign,
    product
  ) as CurrentCampaign;

  const userReviews = product.userReviews;

  const userReviewsSummary =
    review.userReviews && review.userReviews.summary
      ? review.userReviews.summary
      : product.userReviewSummary;

  const isPreRelease =
    product.releaseDate &&
    Date.parse(product.releaseDate) > new Date().getTime();

  const similarProducts = recommendations?.find(
    item => item?.model === ModelTypeEnum.SIMILAR_PRODUCTS
  );

  const handleCompareLinkClick = () => {
    if (!similarProducts?.recommendations?.length) return null;

    return trackComparisonLinkClick(
      similarProducts.recommendations.length + (product ? 1 : 0),
      'similarProducts'
    );
  };

  const similarProductsCompareLink = getSimilarProductsCompareLink(
    similarProducts,
    product,
    linkTo
  );

  const getRecommendationsLink = (modelType: ModelType) => {
    if (
      modelType === ModelTypeEnum.SIMILAR_PRODUCTS &&
      similarProductsCompareLink
    )
      return {
        title: t('product', 'product-compare.compare'),
        url: similarProductsCompareLink,
        onClick: handleCompareLinkClick,
      };

    if (modelType === ModelTypeEnum.PERSONALIZED)
      return {
        title: t('account', 'menu.settings'),
        url: '/personalization',
      };

    return null;
  };

  const shouldRenderAllTabs = longPageActive(market, product?.category?.id);

  return (
    <>
      {me && me.analyticsAccess && (
        <FeedbackButton position="right">
          {t('feedback', 'button.text')}
        </FeedbackButton>
      )}

      <PageContent withAds>
        <StyledProductPage>
          {currentModal === PRODUCT_ALERT_MODAL_ID && (
            <Modal
              id={PRODUCT_ALERT_MODAL_ID}
              onRequestClose={hideModal}
              width={500}
            >
              <ProductAlertModalContent
                productId={product.id}
                hideModal={hideModal}
                privacyPolicyUri={privacyPolicyUri || undefined}
              />
            </Modal>
          )}
          <ProductDuringPromoBanner
            product={product}
            campaignBanners={campaignBanners}
          />
          <MobileTopAd />

          <ProductBreadCrumb product={product} />

          <ScrollElement name="Top">
            <PriceListFilterContextContainer>
              <ViewportContext.Consumer>
                {({ isMobile }) => (
                  <>
                    <ProductPageHeader
                      product={product}
                      isHeaderVisible={isHeaderVisible}
                      setHeaderVisible={setHeaderVisible}
                      currentCampaign={currentCampaign}
                    />

                    <ProductScrollTopButton />

                    <ScrollElement name="Content">
                      <StickyTrack
                        topMargin={0}
                        headerHeight={getHeaderHeight(isMobile)}
                        renderElement={
                          shouldRenderAllTabs ? (
                            <ProductNavigation
                              product={product}
                              selectedCondition={selectedCondition}
                            />
                          ) : (
                            <TabList
                              routeSection={routeSection}
                              product={product}
                              isSticky={!isHeaderVisible}
                              selectedCondition={selectedCondition}
                            />
                          )
                        }
                      >
                        <TabContentWrapper spacingTop={routeSection !== 'main'}>
                          <ProductTabs
                            shouldRenderAllTabs={shouldRenderAllTabs}
                            routeSection={routeSection}
                            isPreRelease={isPreRelease}
                            product={product}
                            similarProducts={similarProducts}
                            importantDates={importantDates}
                            fetchMoreData={fetchMoreData}
                            getRecommendationsLink={getRecommendationsLink}
                            userReviews={userReviews}
                            currentCampaign={currentCampaign}
                            userReviewsSummary={userReviewsSummary}
                          />

                          <Panel>
                            <AdContainer
                              placement="panorama_target"
                              format="panorama_3_wde"
                              min={861}
                              max={10000}
                              scalable
                              scaleBase=".pj-ui-panel--row"
                              triggerPoint={100}
                            />
                          </Panel>

                          <ProductCampaignDeals product={product} />

                          <section>
                            <Lazy offset={100}>
                              {recommendations?.map(item => {
                                if (!item.recommendations?.length) return null;
                                return (
                                  <Recommendations
                                    key={`recommendations-model-${item.model}`}
                                    link={getRecommendationsLink(
                                      item.model as ModelType
                                    )}
                                    recommendations={item.recommendations}
                                    type={item.model as ModelType}
                                    contextId={PRODUCT_CONTEXT_ID}
                                  />
                                );
                              })}
                            </Lazy>

                            <Lazy offset={100}>
                              <RecentlyVisitedProducts offset={1} />
                            </Lazy>
                          </section>
                          <ProductBeforePromoBanner
                            campaignBanners={campaignBanners}
                          />
                        </TabContentWrapper>
                      </StickyTrack>
                    </ScrollElement>
                  </>
                )}
              </ViewportContext.Consumer>
            </PriceListFilterContextContainer>
          </ScrollElement>
        </StyledProductPage>
      </PageContent>
    </>
  );
};

ProductPage.graphQL = productPageQuery;

// Group on product ID, ignore section param
ProductPage.group = ({ name, match }: Group) =>
  `${name}_${match && match.params ? match.params.id : 'unknown'}`;

ProductPage.variables = ({
  id,
  offset,
  section,
  marketCode,
  experiments,
}: Variables) => {
  const excludeTypes = Object.keys(initialFilterState)
    .filter(option => !initialFilterState[option])
    .map(filter => mapOptionsToFilters[filter])
    .filter(x => x);

  const expertsEnabled = ['se'].includes(marketCode);

  return {
    id: parseInt(id, 10),
    offset: parseInt(offset, 10) || 0,
    section,
    statisticsTime: section === 'statistics' ? '1970-01-02' : undefined,
    marketCode,
    personalizationExcludeCategories: readPersonalizationSettings().notInterestedIn.categories.map(
      (category: { id: string }) => +category.id
    ),
    recommendationsContextId: PRODUCT_CONTEXT_ID,
    includeSecondary: false,
    excludeTypes,
    variants: null,
    advized: expertsEnabled,
    condition: 'new',
  };
};

ProductPage.reducer = (
  state: RouteReducerState,
  action: RouteReducerActions
) => {
  switch (action.type) {
    case 'PRICE_FILTER':
      return {
        ...state,
        options: action.options,
        variants: action.variants,
        condition: action.condition,
      };

    case 'PRODUCT_VARIANTS':
      return {
        ...state,
        variants: action.variants,
        options: action.options,
        condition: action.condition,
      };

    case 'PRODUCT_CONDITION':
      return {
        ...state,
        condition: action.condition,
        options: action.options,
      };

    default:
      return state;
  }
};

ProductPage.stateToVariables = (state: RouteReducerState) => {
  if (!state || !state.options) return {};

  const excludeTypes = Object.keys(state.options)
    .filter(option => !state.options[option])
    .map(filter => mapOptionsToFilters[filter])
    .filter(x => x);

  return {
    ...state,
    excludeTypes,
    includeSecondary: state.options?.showInternational,
    priceList: true,
    variants: state.variants,
    condition: state.condition,
  };
};

ProductPage.loadOrder = (
  { section = 'main', marketCode = undefined } = {},
  loaded = {} as Loaded,
  options = { initialRoute: false }
) => {
  const sectionContent = {
    main: {
      priceList: true,
    },
    properties: {
      productProperties: true,
    },
    reviews: {
      userReviews: true,
    },
    statistics: {
      priceHistory: true,
      importantDates: true,
      priceHistoryTable: true,
      priceTrend: true,
      medianPrice: true,
    },
  };

  return [
    {
      main: !loaded.main,
      ...sectionContent[section],
    },
    {
      // Refresh price list for cached pages only
      priceList: options.initialRoute && section === 'main',
      userActions: !loaded.userActions,
      badges: !loaded.badges,
      media: !loaded.media,
      campaign: !loaded.campaign,
      relatedProducts: !loaded.relatedProducts,
      campaignDeals: !loaded.campaignDeals,
      priceHistory: !loaded.priceHistory,
    },
  ];
};

ProductPage.mergeData = (
  oldData: ProductPageQuery,
  newData: ProductPageQuery
) => {
  const data = mergeWith({}, oldData, newData);

  // If price data was refetched always use data from latest fetch
  if (newData?.product?.prices) {
    return {
      ...data,
      product: {
        ...data.product,
        prices: newData.product.prices,
        variantGroups: newData.product.variantGroups,
      },
    };
  }

  return data;
};

export default connect(mapStateToProps, dispatchProps)(ProductPage);
